import { Combobox } from "@/components/ui/combobox";
import { sortWorkersByLastName, lastNameOf } from "@/lib/comboboxHelpers";
import { lastFirst } from "@/lib/names";

/**
 * Worker picker sorted by LAST NAME. Filters as the user types by name,
 * email, or surname alone. Optionally shows a "+ New Contractor" entry
 * pinned at the top of the list — pick the newValue to signal "add-new".
 *
 * When `filter` is provided, the workers list is filtered before sorting
 * (used by Users.jsx which hides workers who already have a login).
 */
export function WorkerCombobox({
  value,
  onChange,
  workers,
  placeholder = "Select a worker…",
  searchPlaceholder = "Type name or email…",
  withNewOption = false,
  newLabel = "+ New Contractor",
  newValue = "__new__",
  filter,
  disabled = false,
  required = false,
  testId,
}) {
  const list = typeof filter === "function"
    ? (workers || []).filter(filter)
    : (workers || []);
  const sorted = sortWorkersByLastName(list);
  const options = [];
  if (withNewOption) {
    options.push({
      value: newValue,
      label: newLabel,
      // Shown in the trigger button once the user has picked "add new".
      // Different from `label` so it doesn't LOOK like the actual name field.
      selectedLabel: "New contractor — enter details below ↓",
      keywords: ["new", "contractor", "add"],
      highlight: true,
      testId: "combobox-worker-new",
    });
  }
  for (const w of sorted) {
    const busy = Array.isArray(w.current_assignments) && w.current_assignments.length > 0;
    const inactive = w.status && w.status !== "active";
    const tags = [inactive && "inactive", busy && `on ${w.current_assignments.length === 1 ? w.current_assignments[0].project_name : `${w.current_assignments.length} jobs`}`].filter(Boolean).join(" · ");
    options.push({
      value: w.id,
      label: lastFirst(w.name) || "(unnamed)",
      sub: [w.email || "", tags].filter(Boolean).join(" — "),
      keywords: [w.email || "", w.name || "", lastNameOf(w.name)],
    });
  }
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyText="No matching worker."
      disabled={disabled}
      required={required}
      testId={testId}
    />
  );
}
